import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { ArticleCard } from '../components/articles/ArticleCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Search, X } from 'lucide-react';

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearched(true);
    try {
      const res = await searchAPI.search(searchQuery);
      setResults(res.data.articles);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchParams({});
    setResults([]);
    setSearched(false);
  };

  return (
    <Layout>
      {/* Search Header */}
      <section className="py-12 md:py-16 border-b border-border">
        <div className="container-custom">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-center">
            Search The Trade
          </h1>
          
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-12 pr-20 h-14 text-lg"
                data-testid="search-page-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-14 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded"
                  data-testid="clear-search-btn"
                >
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              )}
              <Button 
                type="submit" 
                className="absolute right-2 top-1/2 -translate-y-1/2"
                data-testid="search-submit-btn"
              >
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="py-12" data-testid="search-results">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-video w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-6 w-full" />
                </div>
              ))}
            </div>
          ) : searched ? (
            results.length > 0 ? (
              <>
                <p className="text-muted-foreground mb-8">
                  Found {results.length} result{results.length !== 1 ? 's' : ''} for "{searchParams.get('q')}"
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {results.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <h2 className="font-serif text-2xl font-bold mb-4">No Results Found</h2>
                <p className="text-muted-foreground">
                  No articles match "{searchParams.get('q')}". Try a different search term.
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              Enter a search term to find articles
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SearchPage;
